import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processSession } = useAuth();
  const hasProcessed = useRef(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (sessionId) {
      console.log('[AuthCallback] Processing session:', sessionId.substring(0, 8) + '...');
      processSession(sessionId)
        .then((user) => {
          console.log('[AuthCallback] Session processed, role:', user?.role);
          // Redirect based on role
          if (user.role === 'admin' || user.role === 'superadmin') {
            navigate('/admin', { replace: true, state: { user } });
          } else if (user.role === 'sales') {
            navigate('/ventas', { replace: true, state: { user } });
          } else if (user.role === 'professional') {
            navigate('/professional/dashboard', { replace: true, state: { user } });
          } else if (user.role === 'center') {
            navigate('/center/dashboard', { replace: true, state: { user } });
          } else if (user.role === 'workshop') {
            navigate('/workshop/dashboard', { replace: true, state: { user } });
          } else {
            navigate('/select-role', { replace: true, state: { user } });
          }
        })
        .catch((err) => {
          console.error('[AuthCallback] Session processing failed:', err?.response?.data || err.message);
          setAuthError(err?.response?.data?.detail || err.message || 'Error de autenticación');
          // Wait 3 seconds then redirect to login
          setTimeout(() => navigate('/login', { replace: true }), 3000);
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [location.hash, processSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-center">
        {authError ? (
          <>
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-400 text-xl">!</span>
            </div>
            <p className="text-red-400 font-medium mb-2">Error de autenticación</p>
            <p className="text-slate-500 text-sm">{authError}</p>
            <p className="text-slate-600 text-xs mt-2">Redirigiendo al login...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto mb-4" />
            <p className="text-slate-400">Procesando autenticación...</p>
          </>
        )}
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated, processSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessedSession = React.useRef(false);

  // Process session_id from URL hash (Google Auth callback)
  useEffect(() => {
    if (hasProcessedSession.current) return;
    
    const hash = location.hash;
    if (hash && hash.includes('session_id')) {
      hasProcessedSession.current = true;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        processSession(sessionId)
          .then((userData) => {
            // Clear the hash from URL
            window.history.replaceState(null, '', location.pathname);
            // Redirect based on role
            if (userData.role === 'admin' || userData.role === 'superadmin') {
              navigate('/admin', { replace: true });
            } else if (userData.role === 'professional') {
              navigate('/professional/dashboard', { replace: true });
            } else if (userData.role === 'center') {
              navigate('/center/dashboard', { replace: true });
            } else if (userData.role === 'workshop') {
              navigate('/workshop/dashboard', { replace: true });
            } else if (userData.role === 'sales') {
              navigate('/ventas', { replace: true });
            }
            // If role is 'patient' or undefined, stay on current page (select-role)
          })
          .catch(() => {
            navigate('/login', { replace: true });
          });
      }
    }
  }, [location.hash, location.pathname, processSession, navigate]);

  useEffect(() => {
    if (!loading && !isAuthenticated && !location.hash.includes('session_id')) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [loading, isAuthenticated, navigate, location.pathname, location.hash]);

  useEffect(() => {
    if (!loading && isAuthenticated && requiredRole) {
      const userRole = user?.role;
      if (requiredRole === 'admin' && userRole !== 'admin') {
        navigate('/dashboard', { replace: true });
      }
      if (requiredRole === 'professional' && !['professional', 'admin'].includes(userRole)) {
        navigate('/dashboard', { replace: true });
      }
      if (requiredRole === 'center' && !['center', 'admin'].includes(userRole)) {
        navigate('/dashboard', { replace: true });
      }
      if (requiredRole === 'workshop' && !['workshop', 'admin'].includes(userRole)) {
        navigate('/dashboard', { replace: true });
      }
      if (requiredRole === 'sales' && !['sales', 'admin'].includes(userRole)) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [loading, isAuthenticated, user, requiredRole, navigate]);

  if (loading || location.hash.includes('session_id')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};
